import React, { PropsWithChildren, ReactElement, cloneElement, useEffect, useRef, useState } from 'react';
import { Button } from './Button/Button';
import { Dialog, DialogProps } from './Dialog';
import { Input, Checkbox, FormControlLabel } from '@mui/material';

type ConfirmDialogProps = PropsWithChildren<{
	title?: string;
	text?: string | React.ReactNode;
	open?: boolean;
	confirmLabel?: string;
	cancelLabel?: string;
	primaryAction?: 'confirm' | 'cancel';
	// If string, an input box will force the user to type the string to confirm
	dangerous?: boolean | string;
	// Provide a unique key to show a "Don't show again" checkbox and save the user's choice
	dontShowAgain?: string;
	onOpen?: () => void;
	onConfirm?: () => void;
	onCancel?: () => void;
	onAny?: (confirmed: boolean) => void;
	// Use to render the button that will open the dialog in case it can't be the first child. Use the show function to open the dialog.
	render?: (show: () => void) => ReactElement;
	children?: ReactElement;
} & DialogProps>;

/**
 * A convenience Dialog component that can wrap a button and hijack its onClick
 * handler to open the confirmation dialog instead. Pressing confirm will call
 * the original onClick handler.
 */
export function ConfirmDialog({
	open = false,
	title,
	text,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	primaryAction = 'confirm',
	dangerous,
	dontShowAgain,
	onOpen,
	onConfirm,
	onCancel,
	onAny,
	render,
	children,
	...rest
}: ConfirmDialogProps & React.HTMLAttributes<HTMLDivElement>) {
	// Take control over open state if has children
	const [isOpen, setIsOpen] = useState(children ? false : open);
	const [inputValue, setInputValue] = useState('');
	const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);

	useEffect(() => {
		if (open) {
			onOpen?.();
		}
	}, [open, onOpen]);

	// Hijack child onClicks to open the dialog and replay them on confirm
	const child = children && React.Children.only(children);
	const originalOnClick = child?.props.onClick;
	const onClickEvent = useRef<React.MouseEvent>(null);
	let childClone;
	if (children) {
		childClone = cloneElement(children as React.ReactElement, {
			onClick: (e: React.MouseEvent) => {
				e.preventDefault();
				onClickEvent.current = e;
				setIsOpen(true);
				onOpen?.();
			},
		});
	}

	const handleCancel = () => {
		onCancel?.();
		onAny?.(false);
		setIsOpen(false);
	};

	const handleConfirm = () => {
		onConfirm?.();
		onAny?.(true);
		originalOnClick?.(onClickEvent.current);
		onClickEvent.current = null;
		setIsOpen(false);
		if (dontShowAgain && dontShowAgainChecked) {
			localStorage.setItem(`confirm-dialog-dont-show-${dontShowAgain}`, 'true');
		}
	};

	useEffect(() => {
		const onKeyDown = (e) => {
			if (!isOpen) return;
			if (e.key === 'Enter') {
				if (primaryAction === 'confirm') {
					handleConfirm();
				} else if (primaryAction === 'cancel') {
					handleCancel();
				}
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen]);

	// Check if we should skip showing the dialog
	useEffect(() => {
		if (dontShowAgain && localStorage.getItem(`confirm-dialog-dont-show-${dontShowAgain}`)) {
			handleConfirm();
		}
	}, [dontShowAgain]);

	return (
		<>
			{childClone}
			{render && render(() => {
				setIsOpen(true);
				onOpen?.();
			})}
			<Dialog
				open={isOpen}
				title={title}
				text={(
					<>
						{text}
						{typeof dangerous === 'string' && (
							<>
								<span>Type &quot;<span className="font-bold">{dangerous}</span>&quot; below to confirm</span>
								<Input
									value={inputValue}
									onChange={(e) => setInputValue(e.target.value)}
								/>
							</>
						)}
						{dontShowAgain && (
							<FormControlLabel
								control={(
									<Checkbox
										checked={dontShowAgainChecked}
										onChange={(e) => setDontShowAgainChecked(e.target.checked)}
									/>
								)}
								label="Don't show this again"
							/>
						)}
					</>
				)}
				onClose={handleCancel}
				buttons={(
					<>
						<Button
							onClick={handleCancel}
							variant={primaryAction === 'cancel' ? 'filled' : 'outlined'}
						>
							{cancelLabel}
						</Button>
						<Button
							onClick={handleConfirm}
							variant={primaryAction === 'confirm' ? 'filled' : 'outlined'}
							dangerous={!!dangerous}
							disabled={typeof dangerous === 'string' && inputValue !== dangerous}
						>
							{confirmLabel}
						</Button>
					</>
				)}
				{...rest}
			/>
		</>
	);
}
